// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

export enum UserImageType {
    Initials = 'initials',
    Signature = 'signature',
}

export enum UserImageAction {
    Edit = 'edit',
    ReadOnly = 'readonly',
}

export enum UserSignatureCall {
    initials = 2,
    signature = 1,
}

export enum UserNeedProfileUpdate {
    false = 0,
    true = 1,
}
export interface User {
    userId?: number;
    dealerId?: number;
    dealerCount?: number;
    appPermissionIds?: UserPermissionIds[];
    dealerAppPermissions: DealerAppPermissions[];
    availablePermissions?: AppPermission[];
    email: string;
    name?: string;
    firstName: string;
    middleName?: string;
    lastName: string;
    legalName?: string;
    logonId: string;
    phone: string;
    userType: UserTypes;
    needProfileUpdate: boolean;
    needToAcceptSystemDocument: boolean;
    emailAlerts: boolean;
    textAlerts: boolean;
    userDealers: UserDealers[];
    firstSuccessfulLogon?: Date;
    lastLogonDate?: Date;
    lastLogonMessage?: string;
    lastUpdateDate?: Date;
    lastUpdatedBy?: number;
    defaultPageId: number;
    enableMFA?: boolean;
    externalUserId?: string;
    lastWelcomeNotificationSent?: Date;
    jobTitle?: string;
    initials?: string;
    signature?: string;
    active?: boolean;
    sendToWelcomeFlow?: boolean;
    systemAdministratorAtDealerId: boolean;
    dealerActivator: boolean;
    passwordValidation: Date;
    mfaValidation: Date;
    systemDocumentsValidation: Date;
    settingValidation: Date;
}

export interface UserDealers {
    dealerId: number;
    homeDealer: boolean;
    name: string;
    oemCode: string;
    oemName: string;
    oemDealerId: string;
    dealerCodeDisplay?: string;
    userId?: number;
}

export interface UserDealershipTerminationDetails {
    isDealershipTerminated: boolean;
    hasOtherDealershipsAvailable: boolean;
}

export interface UserToken {
    tokenId: number;
    token: string;
    shortToken: string;
    dealerId: number;
    userId: number;
    userType: UserTypes;
    dealId: number;
}

export type GetUserByEmailResponse = { user: User } & UserDealershipTerminationDetails;
export type UserEnrollmentResponse = { user: User; temporaryToken: string };

export enum UserTypes {
    Internal = 1, //DDJAdmin
    Dealer = 2,
    Customer = 3,
    //Buyer and CoBuyer are not real users in DDJ. Real users are Internal, Dealer and Customer.
    //Buyer and CoBuyer are generally used only when needing to differentiate between a type of customer on a deal.
    Buyer = 4,
    CoBuyer = 5,
}

export const SystemUserId = 1;

export enum UserPermissionIds {
    PrintToDDJ = 8,
    UserAdmin = 9,
    DealerInfoManagement = 10,
    DDJAdmin = 12,
    Archive = 13,
    Deals = 160,
    DealsDelete = 170,
    DealsFinalizeAndClose = 180,
    DocumentStorage = 190,
    FAndIForms = 200,
    DocumentStorageDelete = 210,
    DocumentStorageCloseAndArchive = 220,
    ConsumerDocumentsDelete = 230,
    ConsumerDocumentsCloseAndArchive = 240,
}

export enum UserAuthState {
    init = 'init',
    authenticated = 'authenticated',
    loadingUser = 'loadingUser',
    userLoaded = 'userLoaded',
    loggedOut = 'loggedOut',
    error = 'error',
    stale = 'stale',
}

export enum UserLogonState {
    MFASuccess = 1,
    AuthenticationFailure = 2,
    PasswordSuccessMFAPending = 3,
    DealerNotEnrolled = 4,
    LogonProcessComplete = 5,
    MFAFailure = 6,
    SignaturesUpdated = 7,
    SystemDocumentsAccepted = 8,
    Success = 9,
    PasswordResetRequested = 10,
    PasswordResetSuccess = 11,
    PasswordChangeSuccess = 12,
    PasswordChangeFailure = 13,
}

export interface DealerAppPermissions {
    dealerId: number;
    DealerName?: string;
    userPermissionId?: number[];
    homeDealer: boolean;
    oemCode?: string;
    oemName?: string;
    OEMDealerID?: string;
}

export interface JobTitle {
    jobTitle: string;
    canAcceptDpa: boolean;
}

export interface AppPermission {
    internalUseOnly: boolean;
    permission: string;
    permissionDescription: string;
    shortDescription: string;
    userPermissionId: number;
    active: boolean;
}

export enum BuyerRoutes {
    deal = 1,
    info = 2,
}

export enum DocActions {
    sign = 'sign',
    upload = 'upload',
    view = 'view',
}

export enum UserNotificationType {
    Email,
    Text,
    Both,
}

export enum AccountType {
    stellantisBuyer = 'stellantisBuyer',
    stellantisDealer = 'stellantisDealer',
    ddjBuyer = 'ddjBuyer',
    ddjDealer = 'ddjDealer',
    ddjInternal = 'ddjInternal',
}

export enum Tenant {
    stellantis = 'eshop',
    stellantisEZDOCS = 'ezdocs123',
    ddj = 'ddj',
}

export enum OEM {
    default = 'DEFLT',
    stellantis = 'S',
    mitsubishi = 'M',
}

export enum DealerHomePage {
    deals = 1,
    leads = 2,
    consumerDocuments = 3,
}

export enum SystemDocumentTypes {
    tos = 1,
    privacy = 2,
    releaseNotes = 3,
}

export interface UserAuditTrail {
    userId?: number;
    runDate: any;
    activityDate?: any;
    actionPerformedBy?: string;
    actionPerformed?: string;
    iconName: string;
}
